import { z } from "zod";
import { defaultTotalCapacity, totalCapacitySchema, totalCcuSchema } from "./account-capacity.js";
import { accessLevelSchema, DEFAULT_AUTHENTICATED_ACCESS } from "./access.js";
import { isoDateTimeSchema } from "./profile.js";
import { MAX_SPATIAL_SUBSPACES } from "./server-connection.js";
/**
 * Backend-authored document at `entitlements/{uid}`.
 *
 * Clients can never write or derive their own entitlement; the control API
 * writes it after token verification. Access level drives authorization.
 */
export const entitlementSchema = z.object({
    uid: z.string().min(1),
    accessLevel: accessLevelSchema,
    limits: z.object({
        totalCapacity: totalCapacitySchema.optional(),
        totalCcu: totalCcuSchema.optional(),
        maxServers: z.number().int().min(0),
        maxSubspaces: z.number().int().min(0).max(MAX_SPATIAL_SUBSPACES),
        maxSubscribers: z.number().int().min(0),
    }),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
/**
 * Normalizes a Firestore Timestamp-like value (Admin SDK or client SDK — both
 * expose `.toDate()`) to an ISO string; anything else passes through
 * unchanged. Entitlement documents are written with real `Date`s (via
 * `scripts/bootstrap-entitlement.mjs` or the Firebase console), which
 * Firestore stores as Timestamps, while {@link entitlementSchema} expects
 * ISO-8601 strings. Both the control API (Admin SDK reads) and the web app
 * (client SDK reads) must apply this before validating, or a well-formed
 * entitlement fails validation and silently falls back to `guest`.
 */
export function toIsoString(value) {
    if (value !== null &&
        typeof value === "object" &&
        typeof value.toDate === "function") {
        return value.toDate().toISOString();
    }
    return value;
}
/** Normalizes timestamps and caps valid legacy subspace metadata on reads without changing the stored entitlement. */
export function normalizeEntitlementDoc(uid, data) {
    let limits = data?.limits;
    if (limits !== null && typeof limits === "object" && !Array.isArray(limits)) {
        const maxSubspaces = limits.maxSubspaces;
        if (typeof maxSubspaces === "number" &&
            Number.isSafeInteger(maxSubspaces) &&
            maxSubspaces > MAX_SPATIAL_SUBSPACES) {
            limits = { ...limits, maxSubspaces: MAX_SPATIAL_SUBSPACES };
        }
    }
    return {
        ...data,
        uid,
        limits,
        createdAt: toIsoString(data?.createdAt),
        updatedAt: toIsoString(data?.updatedAt),
    };
}
/** A safe default entitlement used until a real entitlement record exists. */
export function defaultEntitlement(uid, accessLevel = DEFAULT_AUTHENTICATED_ACCESS) {
    const now = new Date().toISOString();
    return {
        uid,
        accessLevel,
        limits: {
            maxServers: 0,
            maxSubspaces: accessLevel === "guest" ? 0 : MAX_SPATIAL_SUBSPACES,
            maxSubscribers: 0,
            totalCapacity: defaultTotalCapacity(accessLevel),
        },
        createdAt: now,
        updatedAt: now,
    };
}
//# sourceMappingURL=entitlement.js.map